import { useField, useFormikContext } from 'formik'
import { getPositionMenuItemStyles } from '@user/utils/usePositionMenuItemStyles'
import { useMemo } from 'react'

import { useGetOwnerList } from '@user/store/salesTeam/hooks/useGetOwnerList'
import { useSelector } from 'react-redux'
import { selectAllSalesTeamsUsers } from '@user/store/salesTeam/slice'
import { useGetEligibleUsersForSalesTeams } from '@user/store/users/hooks/useGetEligibleUsersForSalesTeams'
import { ENTITY_TYPES } from '@sales-center/common'
import { OwnerListOption } from '@user/components/Owner/OwnerSelect/types'

/**
 * The two shapes that reach the owner list: options already built for the dropdown, which key
 * on `id`, and sales-team membership rows, which key on `userId`. Both are read only for the
 * identifier, so this names exactly that much rather than pulling in either full type.
 */
type OwnerIdentity = { id?: string | null; userId?: string | null }
import { useTheme } from '@mui/material/styles'
import { LeadAssignmentRule } from '@user/components/Settings/LeadAssignmentRule/LeadAssignmentRuleForm/typings'
import {
  DEFAULT_GUID_VALUE,
  UNASSIGNED_OWNER_ID,
} from '@user/components/Settings/LeadAssignmentRule/constants'
import { ruleNarrowsOwnerOptions } from '@user/components/Settings/LeadAssignmentRule/ownerFieldPolicy'

type Props = {
  errorText?: string
  salesTeamId: string
  required?: boolean
  entityType?: ENTITY_TYPES
  name: string
  owners?: { id?: string; userId?: string; name: string }[]
  unassignedOption?: boolean
  showInactiveUsers?: boolean
  assignmentRuleData?: {
    rule: LeadAssignmentRule | null
    canOverride: boolean
  }
}

export function useData(props: Props) {
  const {
    required,
    entityType,
    salesTeamId,
    name,
    owners,
    unassignedOption,
    showInactiveUsers,
    assignmentRuleData,
  } = props

  const [{ value }, { error, touched }] = useField({
    name,
    validate: (value) => {
      if (required && (!value || value.length === 0)) {
        return 'Required'
      }
    },
  })

  const { setFieldValue } = useFormikContext()

  const showError = Boolean(error && touched)
  const helperText = showError ? error : ''

  const menuItemStyle = getPositionMenuItemStyles(useTheme())

  const ownerList = useGetOwnerList(
    entityType || ENTITY_TYPES.Lead,
    +salesTeamId,
    showInactiveUsers,
  )

  // Every assignable user in the tenant, not just the selected team's. Needed because eligible
  // owners can span teams: two overlapping territories may belong to different sales teams, and
  // the owner of the other one would otherwise be missing from a list built per team -- offered
  // by the server and then invisible in the dropdown.
  //
  // selectAllSalesTeamsUsers, with the plural Teams. The singular selectAllSalesTeamUsers reads
  // state.salesTeam.users, which getSalesTeamUsers(teamId) setAll's for ONE team at a time -- so
  // despite the name it holds whichever team was fetched last, and the add-back loop below could
  // only ever re-add people it already had. That is the per-team list this code exists to escape.
  const allTenantUsers = useSelector(selectAllSalesTeamsUsers)

  // ...and nothing on the Add Lead surfaces fills that collection, so the selector alone would
  // just swap one empty answer for another. Fetch it, but only when it can change the outcome:
  // an approved owner the per-team list does not already contain. In the ordinary single-team
  // case this stays empty and no request is made.
  const teamsToResolve = useMemo(() => {
    const rule = assignmentRuleData?.rule
    const approved = (rule?.approvedOwnerIds ?? []).map((id) =>
      String(id).trim(),
    )

    if (approved.length === 0) return []

    const known = new Set(
      [...(ownerList ?? []), ...allTenantUsers].map(
        (user: OwnerIdentity) => user?.id ?? user?.userId,
      ),
    )

    const missing = approved.some(
      (id) => id && id !== DEFAULT_GUID_VALUE && !known.has(id),
    )

    return missing ? (rule?.allSalesTeamIds ?? []) : []
  }, [assignmentRuleData?.rule, ownerList, allTenantUsers])

  useGetEligibleUsersForSalesTeams(teamsToResolve)

  const options: OwnerListOption[] = useMemo(() => {
    if (!salesTeamId) {
      return []
    }

    const unassigned = { name: 'Unassigned', id: 'unassigned' }
    const preparedOwners =
      owners && unassignedOption
        ? [unassigned, ...owners]
        : owners || [unassigned, ...(ownerList || [])]

    let filteredOwners: any[] = preparedOwners

    // A D2D territory no longer narrows this list (WWM-17508). It used to: a territory was
    // treated as a scope, so the owners of the patch were the only assignable people and even
    // override permission did not widen it. That left the single-owner case as a dropdown with
    // one entry -- a choice in appearance only.
    //
    // A territory now SUGGESTS instead. The owner it supplies is still pre-selected by the form,
    // and the chip above the field names the territory it came from, but the list stays the
    // whole sales team so the suggestion can actually be declined. Assignment RULES are
    // unaffected and still narrow the list unless the user may override them.
    if (
      ruleNarrowsOwnerOptions({
        rule: assignmentRuleData?.rule ?? null,
        canOverrideAutomatedAssignment: Boolean(
          assignmentRuleData?.canOverride,
        ),
      })
    ) {
      filteredOwners =
        filterAssignmentRuleApprovedOwnersWithHierarchy(filteredOwners)
    }

    // Deliberately OUTSIDE the narrowing branch above. Narrowing and reachability are different
    // questions: narrowing asks whether the list should shrink to the approved owners, and a
    // territory answers no -- it suggests rather than dictates. Reachability asks whether an
    // approved owner is in the list at all, and the list is built for ONE sales team, so an
    // owner from the other half of an overlap is missing from it either way.
    //
    // Nesting this inside the narrowing guard meant the overlapping-territory case -- the exact
    // case the add-back exists for, since isTerritoryDerived turns narrowing off -- never ran it.
    const approved = new Set(
      (assignmentRuleData?.rule?.approvedOwnerIds ?? []).map((id: string) =>
        String(id).trim(),
      ),
    )

    if (approved.size > 0) {
      const seen = new Set(
        filteredOwners
          .map((o: OwnerIdentity) => o.id ?? o.userId)
          .filter(Boolean),
      )

      const added: any[] = []
      for (const user of allTenantUsers) {
        if (!approved.has(user.userId) || seen.has(user.userId)) continue
        // Added to `seen` as we go, not once up front: a user on several sales teams appears
        // once PER TEAM in this list, and five rows for one person would become five identical
        // entries in the dropdown.
        seen.add(user.userId)
        // isPosition and level are not decoration. The menu renders an indent of
        // `level * 10`px, so omitting it yields width: "NaNpx", and uses isPosition to decide
        // whether a row is selectable at all.
        added.push({
          id: user.userId,
          name: user.name,
          isPosition: false,
          level: 0,
        })
      }

      filteredOwners = [...filteredOwners, ...added]
    }

    const list = filteredOwners.map((item, index) => ({
      ...item,
      idx: index,
    }))

    return list.map(
      (option: {
        id?: string
        userId?: string
        name: string
        idx: number
      }) => ({
        ...option,
        id: option.id || option.userId || null,
      }),
    )
  }, [
    ownerList,
    owners,
    salesTeamId,
    unassignedOption,
    allTenantUsers,
    filterAssignmentRuleApprovedOwnersWithHierarchy,
  ])

  // when filer approved owners need to maintain the position hierarchy
  function filterAssignmentRuleApprovedOwnersWithHierarchy(data: any[]) {
    const rule = assignmentRuleData?.rule
    const appIds = rule?.approvedOwnerIds || []
    const allIds = rule?.allOwnerIds || []
    const ownerIds =
      appIds.length === 1 && appIds[0] === DEFAULT_GUID_VALUE
        ? [...appIds, ...allIds]
        : appIds
    const approvedOwnerIds: string[] = ownerIds.map((s: any) =>
      String(s).trim(),
    )

    const approved = new Set(
      approvedOwnerIds.map((id) =>
        id === DEFAULT_GUID_VALUE ? UNASSIGNED_OWNER_ID : id,
      ),
    )

    const result: any[] = []

    let positionsRun: any[] = []
    let runEmitted = false
    let prevWasPosition = false

    for (const row of data ?? []) {
      if (row?.isPosition) {
        positionsRun = prevWasPosition ? [...positionsRun, row] : [row]
        runEmitted = false
        prevWasPosition = true

        if (approved.has(row.id)) {
          result.push(row)
        }
        continue
      }

      prevWasPosition = false

      if (approved.has(row.id)) {
        if (positionsRun.length && !runEmitted) {
          result.push(...positionsRun)
          runEmitted = true
        }
        result.push(row)
      }
    }

    return result
  }

  const selectedOption = useMemo(
    () => options.find((option) => option.id === value),
    [options, value],
  )

  return {
    value: selectedOption,
    showError,
    helperText,
    menuItemStyle,
    options,
    ownerList,
    setFieldValue,
  }
}
